import { Fragment } from 'react'
import { WETH, ETH } from '~/constants/addresses'
import { useUpdaterChainId } from '~/hooks/web3'
import { useSpotSwapTokens } from '~/state/dex/hooks'
import { PercentRoute, RouteProtocol } from '@tokenlon/v6-sdk'
import { Address } from 'viem'
import { findTokenByAddress } from '~/utils/token'
import {
  StyledTokenIcon,
  StyledRoutingDiagramWrapper,
  StyledPointLine,
  StyledDiagramProtocolsWrapper,
  StyledProtocolIcon,
  StyledProtocolItemWrapper,
  StyledUniswapBadge,
  StyledProtocolTextIcon,
  StyledTokenTextIcon,
} from './styled'
import { protocolToIconMap } from './index'
import { ReactComponent as IconDotsLine } from '~/assets/images/dots-line.svg'
import IconUniswapV2Badge from '~/assets/images/uniswap-v2-badge.svg'
import IconUniswapV3Badge from '~/assets/images/uniswap-v3-badge.svg'

interface Props {
  fromAddress: Address
  pathRoutes: PercentRoute
}

const getRoutingEntries = (pathRoutes: PercentRoute) => {
  return pathRoutes.map((pathRoute) => {
    const uniqueProtocols = new Set<RouteProtocol>()
    pathRoute.forEach((orderRoute) => {
      uniqueProtocols.add(orderRoute.protocol)
    })
    const outputToken = pathRoute[0].path[pathRoute[0].path.length - 1]
    return { protocols: Array.from(uniqueProtocols), outputToken }
  })
}

const RoutingDiagram: React.FC<Props> = ({ fromAddress, pathRoutes }) => {
  const expectChainId = useUpdaterChainId()
  const spotSwapTokens = useSpotSwapTokens(expectChainId)

  const isInputWETH =
    fromAddress.toLowerCase() === WETH[expectChainId].toLowerCase()
  const inputToken = findTokenByAddress(
    Object.values(spotSwapTokens),
    isInputWETH ? ETH[expectChainId] : fromAddress,
  )

  const routingEntries = getRoutingEntries(pathRoutes)

  return (
    <StyledRoutingDiagramWrapper>
      <StyledPointLine>
        <IconDotsLine />
      </StyledPointLine>
      {inputToken?.logo ? (
        <StyledTokenIcon
          key={inputToken?.address}
          src={inputToken?.logo}
          alt={inputToken?.symbol}
        />
      ) : (
        <StyledTokenTextIcon>
          {inputToken?.symbol?.substring(0, 4)}
        </StyledTokenTextIcon>
      )}
      {routingEntries.map(({ protocols, outputToken }, index) => {
        const isWETH = outputToken.toLowerCase() === WETH[expectChainId]
        const address = isWETH ? ETH[expectChainId] : outputToken
        const token = findTokenByAddress(Object.values(spotSwapTokens), address)
        return (
          <Fragment key={`${outputToken}-${index}`}>
            <StyledDiagramProtocolsWrapper
              wrap="wrap"
              gap={4}
              align="center"
            >
              {protocols.map((protocol, index) => {
                const protocolIcon = protocolToIconMap[protocol]
                return (
                  <StyledProtocolItemWrapper key={`${protocol}-${index}`}>
                    {protocolIcon ? (
                      <StyledProtocolIcon
                        src={protocolIcon}
                        alt={protocol}
                      />
                    ) : (
                      <StyledProtocolTextIcon>
                        {protocol.substring(0, 2).toUpperCase()}
                      </StyledProtocolTextIcon>
                    )}
                    {protocol === 'uniswap v3' ? (
                      <StyledUniswapBadge src={IconUniswapV3Badge} />
                    ) : protocol === 'uniswap v2' ? (
                      <StyledUniswapBadge src={IconUniswapV2Badge} />
                    ) : null}
                  </StyledProtocolItemWrapper>
                )
              })}
            </StyledDiagramProtocolsWrapper>
            {token?.logo ? (
              <StyledTokenIcon
                key={token?.address}
                src={token?.logo}
                alt={token?.symbol}
              />
            ) : (
              <StyledTokenTextIcon>
                {token?.symbol?.substring(0, 4)}
              </StyledTokenTextIcon>
            )}
          </Fragment>
        )
      })}
    </StyledRoutingDiagramWrapper>
  )
}

export default RoutingDiagram
